/**
 * Structured display for a single profile capability explanation.
 * Renders capability summary, feature details, resolution config, category checks, and result.
 */

import React from 'react';
import FontBody2 from '../../../../../components/fonts/font-body2/font-body2';
import { FONT_VARIANT } from '../../../../../components/fonts/font-props';
import { humanizeKey } from '../profile-explanation-utils';
import ProfileExplanationHeader from './components/profile-explanation-header';
import ResolutionConfigSection from './components/resolution-config-section';
import FeatureDetailsSection from './components/feature-details-section';
import CategoryChecksSection from './components/category-checks-section';
import ResultSection from './components/result-section';

import './profile-explanation-display.scss';

export interface IProfileExplanation {
    capability?: { key?: string; class?: string; type?: string };
    feature_details?: {
        feature_id?: number;
        feature_name?: string | null;
        is_enabled?: boolean;
        default_value?: unknown;
        description?: string;
    } | null;
    resolution_config?: {
        resolution_order?: string[];
        skipped_categories?: string[];
        merge_strategy?: string;
    };
    category_checks?: {
        checks?: Array<{
            category?: string;
            status?: string;
            identifier?: string | null;
            rule?: unknown;
            reason?: string;
        }>;
        matching_rules?: Record<string, unknown>;
        rules_count?: number;
    };
    default_value?: unknown;
    result?: unknown;
    is_default?: boolean;
    source?: string;
    has_matching_rules?: boolean;
}

interface IProfileExplanationDisplayProps {
    data: IProfileExplanation;
    defaultExpanded?: boolean;
}

const ProfileExplanationDisplay: React.FC<IProfileExplanationDisplayProps> = ({
    data,
    defaultExpanded = true
}) => {
    const [expanded, setExpanded] = React.useState(defaultExpanded);
    const key = data?.capability?.key ?? 'unknown';
    const type = data?.capability?.type ?? '';
    const source = data?.source ?? '';
    const isDefault = data?.is_default ?? false;
    const featureDetails = data?.feature_details;
    const resolutionConfig = data?.resolution_config;
    const categoryChecks = data?.category_checks;
    const defaultValue = data?.default_value;
    const result = data?.result;

    const matchingCategories = categoryChecks?.checks?.filter((c) => c.status === 'found') ?? [];
    const sourceCategory = matchingCategories.length > 0 
        ? matchingCategories[matchingCategories.length - 1]
        : null;

    return (
        <div className="profile-explanation-display">
            <ProfileExplanationHeader
                capabilityKey={key}
                type={type}
                source={source}
                isDefault={isDefault}
                expanded={expanded}
                onToggle={() => setExpanded(!expanded)}
            />

            {expanded && (
                <div className="profile-explanation-body">
                    {!isDefault && sourceCategory && (
                        <div className="profile-explanation-summary">
                            <FontBody2 variant={FONT_VARIANT.BOLD}>
                                Resolved from: <span className="profile-explanation-summary-source">
                                    {humanizeKey(sourceCategory.category ?? '')} ({sourceCategory.identifier})
                                </span>
                            </FontBody2>
                        </div>
                    )}

                    <div className="profile-explanation-grid">
                        <div className="profile-explanation-grid-left">
                            {resolutionConfig && (
                                <ResolutionConfigSection resolutionConfig={resolutionConfig} />
                            )}

                            {featureDetails != null && (
                                <FeatureDetailsSection featureDetails={featureDetails} />
                            )}
                        </div>

                        <div className="profile-explanation-grid-right">
                            {categoryChecks && (
                                <CategoryChecksSection categoryChecks={categoryChecks} />
                            )}
                        </div>
                    </div>

                    <ResultSection result={result} defaultValue={defaultValue} />
                </div>
            )}
        </div>
    );
};

export default ProfileExplanationDisplay;
